import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

export const rawCommunicationLogRowSchema = z.object({
  id: z.number(),
  /**
   * Which log the row came from. `communication` rows are CORE events from
   * `communication_log`; `audit` rows are Works Lite edits from `audit_trail`
   * (SC-6369 — before this, locally-made changes never appeared in the log at
   * all). Ids are only unique *within* a kind, so render keys must combine both.
   */
  kind: z.enum(['communication', 'audit']),
  direction: z.enum(['inbound', 'outbound']),
  eventType: z.string(),
  source: z.string().nullable(),
  payload: z.unknown(),
  createdTime: z.string(),
  status: z.string(),
  /**
   * `audit` rows only — the user who made the change. Communication rows carry
   * their actor inside the payload instead, so this is null for them.
   */
  actor: z.string().nullable(),
});

export const jobActivityLogResponseSchema = z.array(rawCommunicationLogRowSchema);

export type RawCommunicationLogRow = z.infer<typeof rawCommunicationLogRowSchema>;
export type JobActivityLogResponse = z.infer<typeof jobActivityLogResponseSchema>;

export const jobActivityLogContract = c.router({
  getJobActivityLog: {
    method: 'GET',
    path: '/:jobId/activity-log',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    responses: {
      200: jobActivityLogResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get the combined activity log for a job',
    description:
      'Returns every communicationLog row (CORE events) and auditTrail row (Works Lite ' +
      'edits) for a job, merged and sorted newest-first, without further filtering. ' +
      'The frontend is responsible for label mapping, icon selection, and payload extraction.',
  },
});
